/**
 * ModuleStatsPopover - Display module-specific mapping statistics in a popover
 *
 * A reusable info button that shows mapping coverage for a module's exposed values:
 * - Total exposed values mapped
 * - Areas with mappings
 * - Lines with mappings
 * - Stations with mappings
 *
 * Usage:
 *   <ModuleStatsPopover
 *     totalMappings={42}
 *     areaCount={3}
 *     lineCount={8}
 *     stationCount={12}
 *     isLoading={false}
 *   />
 */

import { Button } from "@/shared/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";
import {
  Info,
  Layers,
  GitBranch,
  Cpu,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Gauge,
} from "lucide-react";

export interface ModuleStatsPopoverProps {
  /** Total number of exposed value mappings for this module */
  totalMappings: number;
  /** Number of unique areas with at least one mapping */
  areaCount: number;
  /** Number of unique lines with at least one mapping */
  lineCount: number;
  /** Number of unique stations with at least one mapping */
  stationCount: number;
  /** Whether stats are still loading */
  isLoading?: boolean;
  /** Icon color class (tailwind) for the primary module icon */
  iconColorClass?: string;
}

export function ModuleStatsPopover({
  totalMappings,
  areaCount,
  lineCount,
  stationCount,
  isLoading = false,
  iconColorClass = "text-primary",
}: ModuleStatsPopoverProps) {
  const hasData = totalMappings > 0;
  const hasGoodCoverage = totalMappings > 5 && (areaCount > 0 || lineCount > 0 || stationCount > 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Info className="h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center gap-2">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : hasGoodCoverage ? (
              <CheckCircle2 className="h-4 w-4 text-success" />
            ) : hasData ? (
              <Info className="h-4 w-4 text-chart-2" />
            ) : (
              <AlertCircle className="h-4 w-4 text-warning" />
            )}
            <h4 className="font-semibold">Mapping Overview</h4>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3">
              {/* Exposed Values Mapped */}
              <StatRow
                icon={<Gauge className={`h-4 w-4 ${iconColorClass}`} />}
                label="Exposed Values"
                value={totalMappings}
                subvalue="mapped"
                color="primary"
              />

              {/* Areas */}
              <StatRow
                icon={<Layers className="h-4 w-4 text-chart-2" />}
                label="Areas"
                value={areaCount}
                subvalue="covered"
                color="blue"
              />

              {/* Lines */}
              <StatRow
                icon={<GitBranch className="h-4 w-4 text-chart-3" />}
                label="Lines"
                value={lineCount}
                subvalue="covered"
                color="cyan"
              />

              {/* Stations */}
              <StatRow
                icon={<Cpu className="h-4 w-4 text-success" />}
                label="Stations"
                value={stationCount}
                subvalue="covered"
                color="green"
              />
            </div>
          )}

          {/* Help text */}
          {!isLoading && !hasData && (
            <p className="text-xs text-muted-foreground text-center pt-2 border-t">
              Open Settings to start mapping exposed values to your factory structure.
            </p>
          )}

          {!isLoading && hasData && !hasGoodCoverage && (
            <p className="text-xs text-muted-foreground text-center pt-2 border-t">
              Continue mapping to improve coverage across more locations.
            </p>
          )}

          {!isLoading && hasGoodCoverage && (
            <p className="text-xs text-success text-center pt-2 border-t">
              Good coverage! Your mappings span multiple levels.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// StatRow Component
// =============================================================================

interface StatRowProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  subvalue?: string;
  color: "primary" | "blue" | "cyan" | "green";
}

function StatRow({ icon, label, value, subvalue, color }: StatRowProps) {
  const bgColors = {
    primary: "bg-primary/10",
    blue: "bg-chart-2/10",
    cyan: "bg-chart-3/10",
    green: "bg-success/10",
  };

  return (
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${bgColors[color]}`}>{icon}</div>
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
        {subvalue && <div className="text-xs text-muted-foreground">{subvalue}</div>}
      </div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

export default ModuleStatsPopover;
